
export interface ResultsStateModel {
    loading: LoadingResultsState,
    selectedResult: ResultModel | null,
    results: ResultModel[],
    error: any | null,
}


export interface ResultModel {
    id: string;
    userId: string;
    firstName: string;
    lastName: string;
    attendanceStatus: string;
    asyncStatus: string;
    /** Async status as returned by the single-result endpoint (note the API spelling). */
    asycnStatus?: string;
    name: string | null;
    fileName: string | null;
    fileUrl: string | null;
    schoolId: string | null;
    postgraduateTrainingProgram: string;
    associatedMedicalSchool: string;
    weeklyScheduleId: string;
    data: {
        communicatesClearly: number,
        usesAppropriateNonVerbal: number,
        gathersRelevantInformation: number,
        prioritizesNeeds: number,
        respondsAppropriately: number,
        reflectsOnSimulations: number,
        analyzePatientNeeds: number,
        identifiesResources: number,
        overallMedicalExpert: number,
        comment: string | null,
        generalObservations: string | null,
        nextSteps: string | null
    },
    application: {
        PGYLevel: string,
        learning: {
            endDate: Date,
            learningNo: string,
            startDate: Date,
        },
        applicationNo: string,
        userName: string,
    },
    /** Individual per-case assessments that the top-level `data` averages. */
    assessments?: ResultAssessment[],
    comment?: string,
    commentBy?: string,
    /** Per-session free-text comments that feed the export's reason columns (E, H). */
    comments?: ResultSessionComment[],
    /** Per-session make-up comments (same shape as `comments`). */
    makeUpComments?: ResultSessionComment[],
    /** Aggregated attendance per module block (Virtual Foundations / In-Person Advanced). */
    attendance?: ResultModuleAttendance,
    /** Aggregated make-up activity completion per module block. */
    makeup?: ResultModuleMakeup,
    /** Aggregated asynchronous activity completion (drives the Additional Modules column). */
    asyncActivities?: ResultAsyncActivities,
    /** Per-session next steps (same shape as `comments`), shown one line per entry. */
    nextSteps?: ResultSessionComment[],
    /** Per-session general observations (same shape as `comments`), shown one line per entry. */
    generalObservations?: ResultSessionComment[],
    isPublished: boolean,
    createdAt: Date
}

export interface ResultSessionComment {
    /** Session/column identifier this comment belongs to. */
    session: string;
    comment: string;
    /** Stage code, e.g. "F10" (Foundations) or "A10" (Advanced). Prefix selects the reason column. */
    stageShort?: string;
}

export interface ModuleAttendance {
    totalSession: number;
    totalPresent: number;
}

export interface ModuleMakeup {
    totalSession: number;
    totalCompleted: number;
}

export interface ResultModuleAttendance {
    foundation: ModuleAttendance;
    advance: ModuleAttendance;
}

export interface ResultModuleMakeup {
    foundation: ModuleMakeup;
    advance: ModuleMakeup;
}

export interface ResultAsyncActivities {
    totalActivities: number;
    totalComplete: number;
}

export interface ResultAssessmentScores {
    communicatesClearly: number;
    usesAppropriateNonVerbal: number;
    gathersRelevantInformation: number;
    prioritizesNeeds: number;
    respondsAppropriately: number;
    reflectsOnSimulations: number;
    analyzePatientNeeds: number;
    identifiesResources: number;
    overallMedicalExpert: number;
    comment: string | null;
}

export interface ResultAssessment {
    data: ResultAssessmentScores;
    id: string;
    createdAt: Date;
    updatedAt: Date;
    facilitatorId: string;
    assessmentStatus: string;
    caseId: string;
    userId: string;
    leaderId: string;
    weeklyScheduleId: string;
    isReleased: boolean;
    generalObservations?: string | null;
    nextSteps?: string | null;
}

export interface ResultBySchoolRequest {
    weeklyScheduleId?: string;
    schoolId: string;
}

export interface SingleResultRequest {
    weeklyScheduleId: string;
    userId: string;
}

export enum LoadingResultsState {
    loadingList = "loadLearningList",
    loadingAddUpdate = "loadLearningForm",
    loadingDelete = "loadLearningDelete",
    notLoading = "notLoadingLearning",
}
